import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ArrowLeft, ShieldCheck, Smartphone } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import { AuthCodeField, AuthError, AuthHeading, authLinkClass, AuthNote, AuthSubmit, getAuthSession, mfaSchema, useAuth, useAuthNavigation } from '@/app/auth';

import { useCodeReset, useCodeStatus, useSingleSubmit } from './-code-reset';

export const Route = createFileRoute('/login/mfa/')({
  beforeLoad: () => {
    const state = getAuthSession().getState();
    // Sem desafio TOTP em andamento, volta para o login (paridade com MfaFormContainer).
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- contrato do TanStack Router
    if (state.status !== 'challenge' || state.challenge !== 'mfa') throw redirect({ to: '/login' });
  },
  component: MfaScreen,
});

type Values = z.infer<ReturnType<typeof mfaSchema>>;

function MfaScreen() {
  const { t } = useLingui();
  const { session, pending, error } = useAuth();
  const follow = useAuthNavigation(session);
  const form = useForm<Values>({ resolver: zodResolver(mfaSchema()), defaultValues: { code: '' } });
  useCodeReset(form, error, pending);
  const codeStatus = useCodeStatus();
  const typed = useWatch({ control: form.control, name: 'code' });
  const { typing } = codeStatus;
  useEffect(() => typing(typed), [typed, typing]);

  const onSubmit = useSingleSubmit(
    form.handleSubmit(async ({ code }) => {
      const state = await session.confirmMfa(code);
      await codeStatus.settle(state);
      await follow(state);
    }),
  );

  return (
    <form className="flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthHeading icon={ShieldCheck} title={<Trans>Confirmação de código MFA</Trans>}>
        <Trans>Abra o aplicativo autenticador e digite o código de 6 dígitos.</Trans>
      </AuthHeading>
      <AuthError message={error} />
      <AuthCodeField
        control={form.control}
        label={t`Código de verificação`}
        error={form.formState.errors.code}
        status={codeStatus.status}
        onComplete={() => {
          if (!pending) void onSubmit();
        }}
      />
      <AuthSubmit pending={pending || codeStatus.status === 'success'}>
        <Trans>Acessar</Trans>
      </AuthSubmit>
      <AuthNote icon={Smartphone}>
        <Trans>
          Caso tenha perdido seu dispositivo, entre em contato com o administrador para definir um novo código de autenticação.
        </Trans>
      </AuthNote>
      <p className="text-center">
        <Link to="/login" className={authLinkClass}>
          <ArrowLeft aria-hidden className="size-4" />
          <Trans>Voltar para o login</Trans>
        </Link>
      </p>
    </form>
  );
}
